/**
 * Source Pool Display Component
 * Shows deduplicated citations at the claim group level
 * Separates shared sources (2+ models) from unique sources (1 model)
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, Users, Sparkles } from 'lucide-react';
import type { DedupedCitation } from '../../utils/citationUtils';
import { formatModelDisplayName } from '../../utils/modelDisplayName';

interface SourcePoolDisplayProps {
  sharedSources: DedupedCitation[];
  uniqueSources: DedupedCitation[];
}

/**
 * Format modelKey (provider:model-name) to user-friendly display name
 * Example: "xai:grok-3-mini" -> "Grok"
 */
const formatModelKey = (modelKey: string): string => {
  // Extract model name from "provider:model-name" format
  const modelName = modelKey.includes(':') ? modelKey.split(':')[1] : modelKey;
  return formatModelDisplayName(modelName);
};

export const SourcePoolDisplay: React.FC<SourcePoolDisplayProps> = ({
  sharedSources,
  uniqueSources
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [sharedExpanded, setSharedExpanded] = useState(true);
  const [uniqueExpanded, setUniqueExpanded] = useState(false);

  const hasShared = sharedSources.length > 0;
  const hasUnique = uniqueSources.length > 0;
  const totalSources = sharedSources.length + uniqueSources.length;

  if (totalSources === 0) {
    return null;
  }

  const renderCitation = (citation: DedupedCitation, isShared: boolean) => {
    const displayUrl = citation.normalizedDomain || citation.text;

    return (
      <div
        key={citation.text}
        className={`flex items-start gap-2 p-2.5 rounded-lg border ${
          isShared
            ? 'bg-cyan-500/10 border-cyan-500/30'
            : 'bg-slate-800/40 border-slate-700/50'
        }`}
      >
        <ExternalLink className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
          isShared ? 'text-cyan-400' : 'text-slate-400'
        }`} />
        <div className="flex-1 min-w-0">
          <a
            href={citation.text}
            target="_blank"
            rel="noopener noreferrer"
            className={`text-sm font-medium hover:underline break-all ${
              isShared ? 'text-cyan-400 hover:text-cyan-300' : 'text-slate-300 hover:text-slate-200'
            }`}
            title={citation.text}
          >
            {displayUrl}
          </a>
          {isShared && citation.citedByModels.length > 0 && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <Users className="w-3 h-3 text-cyan-400" />
              <span className="text-xs text-cyan-300 font-medium">
                Cited by {citation.citationCount} {citation.citationCount === 1 ? 'model' : 'models'}
              </span>
              {citation.citedByModels.length <= 3 && (
                <span className="text-xs text-slate-400">
                  ({citation.citedByModels.map(formatModelKey).join(', ')})
                </span>
              )}
            </div>
          )}
          {!isShared && citation.citedByModels.length > 0 && (
            <span className="text-xs text-slate-500 mt-1 block">
              {formatModelKey(citation.citedByModels[0])}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="mb-4 rounded-lg border border-slate-700/50 bg-slate-800/20 backdrop-blur-sm">
      {/* Header - Collapsible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 border-b border-slate-700/50 hover:bg-slate-700/20 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-slate-400" />
          <h3 className="text-sm font-semibold text-slate-300">
            Research Sources
          </h3>
          <span className="text-xs text-slate-500">
            ({totalSources} {totalSources === 1 ? 'source' : 'sources'})
          </span>
          <div className="ml-auto flex-shrink-0">
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </div>
        {isExpanded && (
          <p className="text-xs text-slate-400 mt-1">
            Citations referenced by models in this claim group
          </p>
        )}
      </button>

      {/* Shared and Unique Sources - Only show when expanded */}
      {isExpanded && (
        <>
          {/* Shared Sources Section */}
          {hasShared && (
            <div className="border-b border-slate-700/50">
              <button
                onClick={() => setSharedExpanded(!sharedExpanded)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/20 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm font-medium text-cyan-300">
                    Shared Sources
                  </span>
                  <span className="text-xs text-slate-400">
                    ({sharedSources.length})
                  </span>
                </div>
                {sharedExpanded ? (
                  <ChevronUp className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                )}
              </button>
              {sharedExpanded && (
                <div className="px-4 pb-3 space-y-2">
                  {sharedSources.map(citation => renderCitation(citation, true))}
                </div>
              )}
            </div>
          )}

          {/* Unique Sources Section */}
          {hasUnique && (
            <div>
              <button
                onClick={() => setUniqueExpanded(!uniqueExpanded)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/20 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <ExternalLink className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-medium text-slate-300">
                    Model-Specific Sources
                  </span>
                  <span className="text-xs text-slate-400">
                    ({uniqueSources.length})
                  </span>
                </div>
                {uniqueExpanded ? (
                  <ChevronUp className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                )}
              </button>
              {uniqueExpanded && (
                <div className="px-4 pb-3 space-y-2">
                  {uniqueSources.map(citation => renderCitation(citation, false))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
